import React, { useMemo, useState } from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { CheckIcon, GridIcon } from '../shared/RelicVaultIcons';
import { COLORS, bodyFont, em, headingFont, panelSurface } from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { ALL_GUILDS_ID, relicsForGuild } from 'utils/relicVault/catalog';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

import { TYPE, FONTS } from 'style/typography';

/**
 * Guild list with owned/total counts. A gold check on the headshot marks a
 * guild whose companion the player owns. Collapses to a horizontal strip on
 * tablet and phone.
 */
const GuildSidebar = ({ catalog, ownedRelicIds, ownedCompanionIds, selectedGuildId, horizontal, onSelect }) => {
  const counts = useMemo(() => {
    const all = relicsForGuild(catalog, ALL_GUILDS_ID, ownedRelicIds);
    const byGuild = {};
    catalog.guilds.forEach(g => {
      const relics = all.filter(r => r.guildId === g.guildId);
      byGuild[g.guildId] = { owned: relics.filter(r => r.owned).length, total: relics.length };
    });
    return {
      all: { owned: all.filter(r => r.owned).length, total: all.length },
      ...byGuild,
    };
  }, [catalog, ownedRelicIds]);

  const rows = (
    <>
      {!horizontal && <Text style={styles.label}>Guilds</Text>}
      <GuildRow
        active={selectedGuildId === ALL_GUILDS_ID}
        horizontal={horizontal}
        name="All Relics"
        count={counts.all}
        onPress={() => onSelect(ALL_GUILDS_ID)}
        thumb={
          <View style={[styles.thumb, styles.allThumb]}>
            <GridIcon size={16} color={COLORS.text} />
          </View>
        }
      />
      {catalog.guilds.map(guild => (
        <GuildRow
          key={guild.guildId}
          active={selectedGuildId === guild.guildId}
          horizontal={horizontal}
          name={guild.name}
          subtitle={guild.title}
          count={counts[guild.guildId]}
          onPress={() => onSelect(guild.guildId)}
          thumb={
            <View
              style={[
                styles.thumb,
                { borderColor: guild.colorHex },
                IS_WEB
                  ? { backgroundImage: iconGlowFor(guild.colorHex) }
                  : { backgroundColor: `${guild.colorHex}33` },
              ]}>
              <Image source={{ uri: guild.headshotUrl }} style={styles.headshot} />
              {ownedCompanionIds.includes(guild.guildId) && (
                <View style={styles.ownedBadge}>
                  <CheckIcon size={8} color="#3A2E00" strokeWidth="3.4" />
                </View>
              )}
            </View>
          }
        />
      ))}
    </>
  );

  if (horizontal) {
    return (
      <View style={[styles.sidebar, styles.sidebarHorizontal]}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {rows}
        </ScrollView>
      </View>
    );
  }
  return <View style={styles.sidebar}>{rows}</View>;
};

const GuildRow = ({ active, horizontal, name, subtitle, count, thumb, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={`${name}, ${count.owned} of ${count.total} relics`}
      style={[
        styles.row,
        horizontal && styles.rowHorizontal,
        hovered && !active && styles.rowHover,
        active && styles.rowActive,
        active && webOnly({ boxShadow: 'inset 0 0 0 1.5px rgba(139,92,246,0.55)' }),
      ]}>
      {thumb}
      <View style={[styles.rowText, horizontal && styles.rowTextHorizontal]}>
        <Text
          style={[styles.name, horizontal && styles.nameHorizontal]}
          numberOfLines={horizontal ? 2 : 1}>
          {name}
        </Text>
        {!!subtitle && !horizontal && (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        )}
        <Text style={styles.count}>
          {count.owned} / {count.total}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  sidebar: {
    ...panelSurface,
    borderWidth: 1.5,
    borderColor: 'rgba(139,111,255,0.24)',
    borderRadius: 20,
    padding: 12,
  },
  sidebarHorizontal: {
    padding: 10,
  },
  label: {
    ...bodyFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.1, 10.5),
    textTransform: 'uppercase',
    color: COLORS.dim,
    paddingTop: 6,
    paddingHorizontal: 10,
    paddingBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    marginTop: 3,
  },
  rowHorizontal: {
    flexDirection: 'column',
    minWidth: 64,
    paddingVertical: 8,
    paddingHorizontal: 6,
    marginTop: 0,
    marginRight: 6,
  },
  rowHover: {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  rowActive: {
    backgroundColor: 'rgba(139,92,246,0.20)',
    borderWidth: IS_WEB ? 0 : 1.5,
    borderColor: 'rgba(139,92,246,0.55)',
  },
  thumb: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  allThumb: {
    borderColor: 'rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  headshot: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  ownedBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#FFC93C',
    borderWidth: 1.5,
    borderColor: '#1A1630',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    marginLeft: 10,
  },
  rowTextHorizontal: {
    flex: 0,
    marginLeft: 0,
    marginTop: 4,
    alignItems: 'center',
  },
  name: {
    ...headingFont('700'),
    fontSize: TYPE.xs,
    color: COLORS.text,
  },
  nameHorizontal: {
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
    textAlign: 'center',
  },
  subtitle: {
    ...bodyFont('600'),
    fontSize: TYPE.xs,
    color: COLORS.lilac,
    marginTop: 1,
  },
  count: {
    ...bodyFont('600'),
    fontSize: TYPE.xs,
    color: COLORS.muted,
  },
});

export default GuildSidebar;
